<template>
    <div>
        <!-- Логовое сообщение (системное событие) -->
        <b-card v-if="isLogMessage" bg-variant="light" body-class="p-2" class="mb-2 activity-log">
            <div class="activity-log__header">
                <div class="activity-log__title">
                    <i class="fas fa-info-circle text-info mr-2"></i>
                    <strong>
                        <TelegramUserLink :user="item.User" :fallback="item.user_id ? 'Автор неизвестен' : 'Система'" />
                    </strong>
                    <span class="activity-log__action">{{ getActionText(item.action) }}</span>
                </div>
                <small class="text-muted activity-log__date">{{ formatDate(item.created_at) }}</small>
            </div>

            <DiscussionHistory :value="displayDescription" :fallback-blocks="descriptionBlocks" source-type="request_history" :source-id="item.id" />
        </b-card>

        <!-- Комментарий пользователя -->
        <div v-else class="border-bottom pb-3 mb-3">
            <div class="d-flex justify-content-between align-items-start">
                <div>
                    <strong>
                        <TelegramUserLink :user="item.Author" fallback="Неизвестный" />
                    </strong>
                    <small class="text-muted ml-2">{{ formatDate(item.created_at) }}</small>
                    <b-badge v-if="item.is_system_comment" variant="info" class="ml-2">Системный</b-badge>
                </div>
                <div v-if="canModifyComment">
                    <i class="fas fa-pen text-success small mr-2 cursor-pointer" 
                       title="Редактировать" 
                       @click="startEdit"
                       v-if="!isEdit"
                    ></i>
                    <i class="fas fa-trash-alt text-danger small cursor-pointer" 
                       title="Удалить" 
                       @click="removeComment"
                       v-if="!isEdit"
                    ></i>
                </div>
            </div>

            <!-- Текст комментария (режим просмотра) -->
            <DiscussionText v-if="!isEdit" :value="item.comment_text || ''" source-type="request_comment" :source-id="item.id" />

            <!-- Редактирование комментария -->
            <div v-else class="mt-2">
                <DiscussionEditor source-type="request_comment" :source-id="item.id" @saved="cancelEdit"
                    v-model="editedText"
                    placeholder="Редактировать комментарий..."
                />
                <div class="mt-2">
                    <b-button size="sm" variant="success" @click="saveEdit">
                        <i class="fas fa-check"></i> Сохранить
                    </b-button>
                    <b-button size="sm" variant="outline-secondary" @click="cancelEdit" class="ml-2">
                        <i class="fas fa-times"></i> Отмена
                    </b-button>
                </div>
            </div>

            <!-- Файлы к комментарию -->
            <div v-if="item.Files && item.Files.length" class="mt-2">
                <small class="text-muted"><i class="fas fa-paperclip"></i> Прикрепленные файлы:</small>
                <FileList 
                    :list="item.Files" 
                    :can-edit="canModifyComment"
                    :request-id="requestId"
                    @remove="removeFile"
                />
            </div>
        </div>
    </div>
</template>

<script>
import FileList from '../FileList/FileList.vue';
import TelegramUserLink from '../components/TelegramUserLink.vue';
import linkifyText from '@/utils/Urlify';
import { getHistoryActionText } from '@/utils/requestCard/formatters';
import { fulfillmentSpeech } from '@/utils/requestCard/historySpeech';

export default {
    name: "CommentItem",
    components: {
        FileList,
        TelegramUserLink
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        canEdit: {
            type: Boolean,
            default: false
        },
        requestId: {
            type: [Number, String],
            required: false
        }
    },
    data() {
        return {
            isEdit: false,
            editedText: ''
        }
    },
    computed: {
        isLogMessage() {
            // Логовое сообщение если есть поле action (история изменений)
            return !!this.item.action;
        },
        displayDescription() {
            return this.getDisplayDescription(this.item.action, this.item.description);
        },
        descriptionBlocks() {
            if (!this.item.user_id && !this.item.User) {
                return [{kind: 'system', text: this.displayDescription}];
            }
            const fulfillment = fulfillmentSpeech(this.item.action, this.displayDescription, this.item.executor_name);
            if (fulfillment) return fulfillment;
            const parts = this.splitDescription(this.item.action, this.displayDescription);
            return [{kind: 'system', text: parts.systemText}, {kind: 'human', text: parts.humanText}]
                .filter(part => part.text);
        },
        canModifyComment() {
            if (!this.canEdit || this.item.is_system_comment) {
                return false;
            }
            if (this.$store.getters.isAdmin) {
                return true;
            }
            const currentUser = this.$store.getters.currentUser || {};
            const authorId = this.item.user_id || (this.item.Author && this.item.Author.user_id);
            return Number(authorId) > 0 && Number(authorId) === Number(currentUser.userId);
        }
    },
    methods: {
        formatLinkedText(text) {
            return linkifyText(text);
        },
        splitDescription(action, description) {
            const text = String(description || '').trim();
            if (!text) {
                return { systemText: '', humanText: '', humanLabel: '' };
            }

            const markerMatch = /(?:Причина(?:\s+срочности)?|Комментарий(?:\s+к\s+согласованию)?|Отч[её]т):\s*/i.exec(text);
            if (markerMatch) {
                const markerText = markerMatch[0].replace(/:\s*$/, '').trim();
                return {
                    systemText: text.slice(0, markerMatch.index).trim(),
                    humanText: text.slice(markerMatch.index + markerMatch[0].length).trim(),
                    humanLabel: markerText.charAt(0).toUpperCase() + markerText.slice(1).toLowerCase()
                };
            }

            // Admin override без отдельной причины/комментария описывает действие системы,
            // даже если само событие обычно допускает пользовательский текст.
            if (/^Admin override:/i.test(text)) {
                return { systemText: text, humanText: '', humanLabel: '' };
            }

            const humanLabels = {
                submitted: 'Комментарий',
                approved: 'Комментарий',
                rejected: 'Причина',
                returned: 'Причина',
                cancelled: 'Причина',
                completed: 'Отчёт',
                partially_complete: 'Отчёт',
                accepted: 'Комментарий',
                accept_partially: 'Комментарий',
                execute: 'Комментарий'
            };
            const humanLabel = humanLabels[action];

            if (humanLabel) {
                return { systemText: '', humanText: text, humanLabel };
            }

            return { systemText: text, humanText: '', humanLabel: '' };
        },
        formatDate(dateString) {
          if (!dateString) return '';
          const date = new Date(dateString);

          const datePart = date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }); // 06.02.2026

          const weekday = date
              .toLocaleDateString('ru-RU', { weekday: 'short' })
              .replace('.', '');

          const timePart = date.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit'
          }); // 14:30

          return `${datePart} ${weekday}, ${timePart}`;
        },
        formatFileSize(bytes) {
            if (!bytes) return '';
            const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
            let i = Math.floor(Math.log(bytes) / Math.log(1024));
            if (i >= sizes.length) i = sizes.length - 1;
            return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
        },
        getActionText(action) {
            if (action === 'fulfillment_review_escalated') {
                if (this.displayDescription.startsWith('Участники не решили вопрос в срок.')) return 'передала вопрос администраторам Аргуса';
                if (this.displayDescription.startsWith('Вопрос пока не решили. Согласующему')) return 'передала вопрос согласующему';
                return 'передала вопрос на следующий уровень разбора';
            }
            return getHistoryActionText(action);
        },
        getDisplayDescription(action, description) {
            if (!description) {
                return '';
            }

            if (action === 'fulfillment_review_escalated') {
                return String(description).replace(/^Участники не решили вопрос в срок\. Нужна ваша помощь\./,
                    'Участники не решили вопрос в срок. Вопрос передан администраторам Аргуса для помощи в решении.');
            }

            if (action !== 'auto_closed_acceptance_timeout') {
                return description;
            }

            const daysMatch = String(description).match(/течение\s+(\d+)\s*дн/i);
            const daysText = daysMatch ? `${daysMatch[1]} дн.` : 'заданного срока';

            return `Заявка автоматически завершена: инициатор не принял результат в течение ${daysText} после перехода в статус "Ожидает приемки".`;
        },
        startEdit() {
            this.editedText = this.item.comment_text;
            this.isEdit = true;
        },
        cancelEdit() {
            this.isEdit = false;
            this.editedText = '';
        },
        saveEdit() {
            if (!this.editedText.trim()) {
                this.$bvToast.toast('Комментарий не может быть пустым', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }
            this.$emit('editComment', { commentId: this.item.id, text: this.editedText });
        },
        hasDirtyState() {
            return this.isEdit && this.editedText !== this.item.comment_text;
        },
        removeComment() {
            this.$bvModal.msgBoxConfirm('Вы уверены, что хотите удалить комментарий?', { contentClass: 'argus-glass-dialog',
                title: 'Подтверждение удаления',
                okVariant: 'danger',
                okTitle: 'Удалить',
                cancelTitle: 'Отмена'
            }).then(confirmed => {
                if (confirmed) {
                    this.$emit('removeComment', this.item.id);
                }
            });
        },
        removeFile(fileId) {
            this.$emit('removeFile', { commentId: this.item.id, fileId });
        }
    }
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.comment-text {
    white-space: pre-wrap;
}

.comment-linked-text {
    overflow-wrap: anywhere;
}

.activity-log__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem 1rem;
}

.activity-log__title {
    min-width: 0;
}

.activity-log__action {
    margin-left: 0.5rem;
}

.activity-log__date {
    flex: 0 0 auto;
    white-space: nowrap;
}

.activity-log__system-text {
    margin: 0.6rem 0 0 1.75rem;
    color: #6c757d;
    font-size: 0.875rem;
    line-height: 1.45;
    white-space: pre-wrap;
}

.activity-log__human-note {
    margin: 0.65rem 0 0 1.75rem;
    padding: 0.65rem 0.8rem;
    border-left: 4px solid #2684ff;
    border-radius: 0.25rem;
    background: #fff;
    box-shadow: 0 1px 2px rgba(31, 45, 61, 0.08);
}

.activity-log__human-label {
    margin-bottom: 0.3rem;
    color: #1769aa;
    font-size: 0.75rem;
    font-weight: 700;
}

.activity-log__human-text {
    color: #212529;
    font-size: 0.95rem;
    font-weight: 500;
    line-height: 1.55;
    white-space: pre-wrap;
}

@media (max-width: 575.98px) {
    .activity-log__header {
        display: block;
    }

    .activity-log__action {
        margin-left: 0.25rem;
    }

    .activity-log__date {
        display: block;
        margin-top: 0.35rem;
        margin-left: 1.75rem;
        white-space: normal;
    }

    .activity-log__system-text,
    .activity-log__human-note {
        margin-left: 0;
    }
}

i.cursor-pointer {
    cursor: pointer;
}

i.cursor-pointer:hover {
    opacity: 0.7;
}
</style>

